/**
 * Display labels for the canonical lifecycle stage enum — the public Raises
 * mirror of `STAGE_LABELS` in
 * `my-backend/src/services/activeRaises/normalization/stageTaxonomy.ts`.
 *
 * Projects carry BOTH a canonical stage (the normalized lifecycle position used
 * for filtering) and an owner-editable raw `stage` string. The directory chips
 * and profile badges prefer the raw label when present (it is what the owner
 * wrote), and fall back to the canonical label otherwise — see
 * {@link displayStageLabel}.
 *
 * WI-3 (2026-07-20 UI overhaul): a raw `stage` is owner-EDITABLE free text, but
 * ingestion sometimes populates it with an un-humanized enum token instead
 * (`seeking_FC`, `preferred_bidder`) — those printed verbatim on the public
 * directory/profile. {@link humanizeEnumToken} + {@link displayStageLabel} close
 * that gap: a raw value is humanized only when it is TOKEN-SHAPED (matches
 * {@link TOKEN_SHAPE} — letters/underscores only, and either contains an
 * underscore or is entirely lowercase), so genuine owner-authored free text
 * ("Reaching Financial Close Q3", "TBD") is never mangled.
 */
import type { CanonicalStage } from '@/types/raises';

/**
 * Known infrastructure-finance shorthand that survives naive title-casing
 * poorly (`Fc` reads as a typo, not "Financial Close"). Matched case-
 * insensitively against each underscore-delimited segment of a token-shaped
 * string; unmatched segments fall back to simple title-casing.
 */
const ENUM_SHORTHAND: Record<string, string> = {
  FC: 'Financial Close',
  NTP: 'Notice To Proceed',
  RFP: 'Request For Proposal',
  COD: 'Commercial Operation Date',
  FID: 'Final Investment Decision',
  EPC: 'EPC',
  DFI: 'DFI',
  IPO: 'IPO',
};

/**
 * Token-shaped: letters and underscores only (mirrors the WI-3 spec regex
 * `/^[a-z_]+$/i`). Matching this alone is not sufficient — see
 * {@link isHumanizableToken}.
 */
const TOKEN_SHAPE = /^[a-z_]+$/i;

/**
 * A token-shaped string is humanized only when it ALSO either contains an
 * underscore (`seeking_FC`) or is entirely lowercase (`financing`) — a bare
 * mixed/upper-case word with no underscore (`TBD`, `FC` standing alone) is
 * genuine short free text, not an un-humanized enum token, and is left as-is.
 */
function isHumanizableToken(value: string): boolean {
  if (!TOKEN_SHAPE.test(value)) return false;
  return value.includes('_') || value === value.toLowerCase();
}

/**
 * Humanizes a token-shaped raw enum string into a readable label: underscores
 * become spaces, each segment is title-cased, and known shorthand (`FC` →
 * "Financial Close") is substituted whole-segment. Exported so other display
 * sites needing the same "raw enum token → readable label" fallback (e.g. the
 * admin `verifiedStatus` field) can reuse it rather than re-implementing.
 *
 * Caller's responsibility: only pass values that are actually token-shaped
 * (see {@link isHumanizableToken}) — this function does no shape-guarding of
 * its own, so it stays a pure, unconditional formatter.
 */
export function humanizeEnumToken(raw: string): string {
  return raw
    .split('_')
    .filter((segment) => segment.length > 0)
    .map((segment) => {
      const shorthand = ENUM_SHORTHAND[segment.toUpperCase()];
      if (shorthand) return shorthand;
      return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Canonical stage → display label. Mirrors the backend `STAGE_LABELS` map
 * field-for-field (ordered by the 1..7 lifecycle ordinal).
 */
export const STAGE_LABELS: Record<CanonicalStage, string> = {
  early_development: 'Early Development',
  development: 'Development',
  permitting: 'Permitting',
  procurement: 'Procurement / Tender',
  financing: 'Financing',
  construction: 'Construction',
  operational: 'Operational',
};

/**
 * Maps a canonical stage value to its display label.
 *  - `null` / `undefined` → `'—'`.
 *  - Unknown value → the raw value verbatim (forward-compatible).
 */
export function stageLabel(stage: string | null | undefined): string {
  if (stage === null || stage === undefined) return '—';
  return STAGE_LABELS[stage as CanonicalStage] ?? stage;
}

/**
 * The stage label to surface on a project: the owner-editable raw `stage`
 * string when present and non-blank, otherwise the canonical stage's display
 * label. Returns `'—'` only when neither is available.
 *
 * WI-3: a raw stage that is TOKEN-SHAPED (see {@link isHumanizableToken}) is
 * humanized before display (`seeking_FC` → "Seeking Financial Close") rather
 * than printed verbatim — genuine owner-authored free text is untouched.
 */
export function displayStageLabel(
  rawStage: string | null | undefined,
  canonicalStage: CanonicalStage | null | undefined,
): string {
  const raw = rawStage?.trim();
  if (raw) return isHumanizableToken(raw) ? humanizeEnumToken(raw) : raw;
  return stageLabel(canonicalStage);
}
